import React, { useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { toast } from "sonner";
import { FileDown, Share, CircleAlert, ChartSpline } from "lucide-react";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import { Card, CardContent } from "@/components/ui";
import { ChartHeader } from "@/components/ChartHeader";
import { ChartTooltipList } from "@/components/ChartTooltipList";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  FunnelStateMaps,
  translateGroupName,
  getGroupNames,
  FUNNEL_STATE_TRANSLATION_KEYS,
  FunnelMapKeys,
  ORDERED_FUNNEL_STATES,
} from "@/util/funnelGroups";
import { useLiveEventDetailsParticipantCounts } from "@/hooks/useLiveEventDetailsParticipantCounts";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { exportChartCSV, timestampFilename } from "@/util/exportChartCSV";

export interface GroupedBarData {
  groupKey: FunnelMapKeys<"CURRENT_FUNNEL_STATE_CHART">;
  label: string;
  count: number;
  details: {
    label: string;
    count: number;
  }[];
}

interface Props {
  eventId: string;
  selectedBarGroup: FunnelMapKeys<"CURRENT_FUNNEL_STATE_CHART"> | null;
  onBarSelection: (
    groupKey: FunnelMapKeys<"CURRENT_FUNNEL_STATE_CHART"> | null,
  ) => void;
  filterState?: FilterState | null;
}

const EXPORT_ORDER_GROUPS: (keyof typeof FunnelStateMaps.CURRENT_FUNNEL_STATE_CHART)[] =
  ["inquired", "applied", "admitted", "commitmentWithdrawn", "committed"];

export const ResultBarChart: React.FC<Props> = ({
  eventId,
  selectedBarGroup,
  onBarSelection,
  filterState,
}) => {
  const { t } = useTranslation();

  const { participantCounts, getGroupedCounts, loading, error } =
    useLiveEventDetailsParticipantCounts(eventId, filterState);

  const groupedData = getGroupedCounts("CURRENT_FUNNEL_STATE_CHART");
  const groupNames = getGroupNames("CURRENT_FUNNEL_STATE_CHART");

  const chartData: GroupedBarData[] = useMemo(() => {
    if (!participantCounts || !groupedData) {
      return [];
    }

    return groupNames.map((groupName) => {
      const count = groupedData[groupName] || 0;
      const groupStates = FunnelStateMaps.CURRENT_FUNNEL_STATE_CHART[groupName];

      const details = sortBy(
        participantCounts.filter(
          (item) => groupStates.includes(item.state) && item.value > 0,
        ),
        (item) => ORDERED_FUNNEL_STATES.indexOf(item.state),
      ).map((item) => ({
        label: t(
          `global.funnelStates.${FUNNEL_STATE_TRANSLATION_KEYS[item.state]}`,
        ),
        count: item.value,
      }));

      const finalDetails =
        details.length > 0
          ? details
          : [
              {
                label: translateGroupName(groupName),
                count: count,
              },
            ];

      return {
        groupKey: groupName,
        label: translateGroupName(groupName),
        count,
        details: finalDetails,
      };
    });
  }, [participantCounts, groupedData, groupNames, t]);

  const filteredData = chartData;
  const hasData =
    participantCounts &&
    participantCounts.length > 0 &&
    filteredData.length > 0;

  const handleExportCSV = useCallback(() => {
    if (!participantCounts || !groupedData) {
      toast.error("No data available to export");
      return;
    }

    try {
      const sortedGroups = sortBy(
        chartData.filter((item) => item.count > 0),
        (item) => EXPORT_ORDER_GROUPS.indexOf(item.groupKey),
      );

      const sortedFunnelStates = sortBy(
        participantCounts.filter((item) => item.value > 0),
        (item) => ORDERED_FUNNEL_STATES.indexOf(item.state),
      );

      const combinedData: Record<string, unknown>[] = [];

      sortedGroups.forEach((group) => {
        combinedData.push({
          group: group.label,
          students: group.count,
        });
      });

      combinedData.push({
        group: "",
        students: "",
      });
      combinedData.push({
        group: "",
        students: "",
      });

      combinedData.push({
        group: t("eventDetails.results.chart.export.headers.funnelState"),
        students: t("eventDetails.results.chart.export.headers.students"),
      });

      sortedFunnelStates.forEach((state) => {
        combinedData.push({
          group: t(
            `global.funnelStates.${FUNNEL_STATE_TRANSLATION_KEYS[state.state]}`,
          ),
          students: state.value,
        });
      });

      exportChartCSV({
        filename: timestampFilename("funnel-chart-counts"),
        headers: [
          t("eventDetails.results.chart.export.headers.group"),
          t("eventDetails.results.chart.export.headers.students"),
        ],
        data: combinedData,
      });

      toast.success(t("global.exportToCSV.success"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("global.exportToCSV.failure"));
    }
  }, [participantCounts, groupedData, chartData, t]);

  const menuOptions = useMemo(
    () => [
      {
        label: t("global.exportToCSV.menuTitle"),
        onClick: handleExportCSV,
        icon: <FileDown aria-hidden height={16} />,
      },
      {
        label: t("eventDetails.results.chart.menuOptions.share"),
        onClick: () => {
          toast.success(t("eventDetails.results.chart.successCopied"));
          navigator.clipboard.writeText(window.location.href);
        },
        icon: <Share aria-hidden height={16} />,
      },
    ],
    [t, handleExportCSV],
  );

  const handleBarClick = (data: GroupedBarData) => {
    const newSelection =
      selectedBarGroup === data.groupKey ? null : data.groupKey;
    onBarSelection(newSelection);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="h-80 p-4">
          <Skeleton className="h-full w-full" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex h-80 items-center justify-center">
          <div className="flex flex-col items-center space-y-3">
            <CircleAlert className="h-12 w-12 text-muted-foreground" />
            <div className="text-center text-sm text-muted-foreground">
              {t("eventDetails.results.chart.loadingError")}
            </div>
          </div>
        </div>
      );
    }

    if (!hasData) {
      return (
        <div className="flex h-[282px] w-full flex-col items-center justify-center">
          <div className="flex flex-col items-center space-y-3">
            <ChartSpline className="h-12 w-12 text-muted-foreground" />
            <div className="text-center text-sm text-muted-foreground">
              {t("eventDetails.results.chart.emptyMessage")}
            </div>
          </div>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={filteredData}>
          <CartesianGrid horizontal vertical={false} stroke="var(--border)" />
          <XAxis
            dataKey="label"
            interval={0}
            tickLine={false}
            axisLine={false}
            tick={{
              fontSize: 12,
              fill: "var(--foreground-medium)",
            }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 12,
              fill: "var(--foreground-medium)",
            }}
          />
          <Tooltip
            cursor={false}
            content={
              <ChartTooltipList
                getColor={(i) => `var(--chart-${(i % 5) + 1})`}
              />
            }
          />
          <Bar
            dataKey="count"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            minPointSize={1}
          >
            {filteredData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  selectedBarGroup && entry.groupKey !== selectedBarGroup
                    ? "var(--chart-1-unselected)"
                    : "var(--chart-1)"
                }
                className="hover:cursor-pointer"
                onClick={() => handleBarClick(entry)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card className="mt-6">
      <ChartHeader
        title={t("eventDetails.results.chart.title")}
        subtitle={
          <span className="sr-only" data-testid="sr-only-subtitle">
            {t("eventDetails.results.chart.ariaSubtitle")}
          </span>
        }
        menuOptions={loading || !!error || !hasData ? undefined : menuOptions}
      />
      <CardContent>{renderContent()}</CardContent>
    </Card>
  );
};
